import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { RoutePicking, RouteStop } from "app/types/route";
import {
  DELIVERY_STATUS_LABELS,
  DRIVER_STATUS_LABELS,
  getRelationId,
  getRelationName,
} from "app/utils/odooDisplay";
import {
  canReleaseStopForReassignment,
  isArchivedRouteStop,
} from "app/utils/routeReassignment";
import { useRouteStopsList } from "./useRouteStopsList";

const DELIVERY_STATUS_CHIP: Record<string, { bg: string; color: string }> = {
  delivered: { bg: "#dcfce7", color: "#166534" },
  in_progress: { bg: "#fef3c7", color: "#92400e" },
  not_delivered: { bg: "#fee2e2", color: "#991b1b" },
};

type RouteStopsTableProps = {
  stops: RouteStop[];
  pickingById: Map<number, RoutePicking>;
  listControls: ReturnType<typeof useRouteStopsList>;
};

export function RouteStopsTable({
  stops,
  pickingById,
  listControls,
}: RouteStopsTableProps) {
  const {
    manualReleaseMode,
    expandedStopIds,
    releasingStopId,
    isReleasingAll,
    toggleStopExpanded,
    releaseStop,
  } = listControls;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1, py: 0.75 }}>
        {stops.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ py: 3, textAlign: "center", color: "#94a3b8", fontSize: "0.9375rem" }}
          >
            No stops on this route
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {stops.map((stop, idx) => {
              const archived = isArchivedRouteStop(stop);
              const releasable = canReleaseStopForReassignment(stop, manualReleaseMode);
              const isExpanded = expandedStopIds.includes(stop.id);
              const isReleasing = releasingStopId === stop.id;
              const address = stop.shipping_address || "—";
              const deliveryStatus = stop.delivery_status || "";
              const deliveryLabel =
                DELIVERY_STATUS_LABELS[deliveryStatus] || deliveryStatus || "—";
              const driverLabel =
                DRIVER_STATUS_LABELS[stop.driver_status || ""] ||
                stop.driver_status ||
                "—";
              const statusChip = DELIVERY_STATUS_CHIP[deliveryStatus] || {
                bg: "#f1f5f9",
                color: "#475569",
              };
              const pickingId = getRelationId(stop.picking_id);
              const linkedPicking =
                pickingId != null ? pickingById.get(pickingId) : undefined;
              const orderDriver = getRelationName(
                linkedPicking?.driver_id ?? stop.driver_id,
                "—"
              );
              const orderState = linkedPicking?.state || stop.picking_id?.state || "—";

              return (
                <Box
                  key={stop.id}
                  sx={{
                    borderRadius: "10px",
                    border: "1px solid",
                    borderColor: releasable ? "#fecaca" : "#e8ecf1",
                    bgcolor: archived ? "#fafbfc" : "#fff",
                    overflow: "hidden",
                    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                    boxShadow: isExpanded
                      ? "0 1px 4px rgba(15, 23, 42, 0.06)"
                      : "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: 0.75,
                      py: 0.85,
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label={isExpanded ? "Collapse stop" : "Expand stop"}
                      onClick={() => toggleStopExpanded(stop.id)}
                      sx={{
                        width: 26,
                        height: 26,
                        p: 0,
                        color: "#64748b",
                        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        flexShrink: 0,
                      }}
                    >
                      <ChevronRightIcon sx={{ fontSize: 20 }} />
                    </IconButton>

                    <Box
                      sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                      onClick={() => toggleStopExpanded(stop.id)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.8125rem",
                            fontWeight: 700,
                            color: "#64748b",
                            flexShrink: 0,
                          }}
                        >
                          #{stop.sequence ?? (idx + 1) * 10}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.9375rem",
                            fontWeight: 700,
                            color: "#0f172a",
                            flexShrink: 0,
                          }}
                        >
                          {stop.picking_id?.name || "—"}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: "0.875rem",
                            color: "#475569",
                            flex: "1 1 80px",
                            minWidth: 0,
                          }}
                        >
                          {getRelationName(stop.partner_id)}
                        </Typography>
                        <Chip
                          size="small"
                          label={deliveryLabel}
                          sx={{
                            height: 22,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            bgcolor: statusChip.bg,
                            color: statusChip.color,
                            flexShrink: 0,
                          }}
                        />
                        {archived && (
                          <Chip
                            size="small"
                            label="Archived"
                            sx={{
                              height: 20,
                              fontSize: "0.6875rem",
                              bgcolor: "#e2e8f0",
                              color: "#475569",
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {releasable && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isReleasing || isReleasingAll}
                        onClick={(event) => {
                          event.stopPropagation();
                          void releaseStop(stop);
                        }}
                        sx={{
                          flexShrink: 0,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          minWidth: 0,
                          px: 1.1,
                          py: 0.25,
                          borderColor: "#fca5a5",
                          color: "#991b1b",
                          bgcolor: "#fff",
                          "&:hover": {
                            borderColor: "#f87171",
                            bgcolor: "#fef2f2",
                          },
                        }}
                      >
                        {isReleasing ? (
                          <CircularProgress size={14} sx={{ color: "#991b1b" }} />
                        ) : (
                          "Release"
                        )}
                      </Button>
                    )}
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        px: 1.5,
                        pb: 1.1,
                        pt: 0,
                        ml: 3.5,
                        borderTop: "1px solid #f1f5f9",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          lineHeight: 1.4,
                          color: "#0f172a",
                          mt: 0.85,
                          wordBreak: "break-word",
                        }}
                      >
                        {getRelationName(stop.partner_id)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          lineHeight: 1.45,
                          color: "#475569",
                          mt: 0.35,
                          wordBreak: "break-word",
                        }}
                      >
                        {address}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1.5,
                          mt: 0.85,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                          <Box component="span" sx={{ fontWeight: 600, color: "#475569" }}>
                            Loads:
                          </Box>{" "}
                          {stop.load_count ?? 0}
                        </Typography>
                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                          <Box component="span" sx={{ fontWeight: 600, color: "#475569" }}>
                            Stop driver:
                          </Box>{" "}
                          {driverLabel}
                        </Typography>
                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                          <Box component="span" sx={{ fontWeight: 600, color: "#475569" }}>
                            Order driver:
                          </Box>{" "}
                          {orderDriver}
                        </Typography>
                        <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                          <Box component="span" sx={{ fontWeight: 600, color: "#475569" }}>
                            Order state:
                          </Box>{" "}
                          {orderState}
                        </Typography>
                      </Box>
                      {releasable && (
                        <Typography
                          sx={{
                            mt: 1,
                            fontSize: "0.75rem",
                            color: "#991b1b",
                            lineHeight: 1.4,
                          }}
                        >
                          This delivery failed. Release it to make the order available for
                          reassignment on a new route.
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
